import React, { useRef } from "react";
import { IconButton, Tooltip, TablePagination } from "@mui/material";
import { Edit, DeleteOutline } from "@mui/icons-material";
import CommonTable, { HeadCell } from "../../../../../business-types/grocery/components/CommonTable";
import Image from "src/shared/Image";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { Category, Product } from "src/shared/services/interfacev2";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

type ProductRow = {
  id: string;
  sno: JSX.Element | number;
  productName: string;
  arabicName: string;
  amount: string;
  categoryName: string;
  imageUrl: JSX.Element;
  Actions: JSX.Element;
};

interface ProductTableProps {
  products: Product[];
  categories: Category[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  showDeletedProducts: boolean;
  onPageChange: (event: any, newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onReorderProduct?: (productId: string, sourceIndex: number, targetIndex: number) => void;
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  categories,
  loading,
  page,
  rowsPerPage,
  totalRecords,
  showDeletedProducts,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onDelete,
  onReorderProduct,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const container = scrollRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const threshold = 60; // distance from top/bottom
    const speed = 12; // scroll speed

    const mouseY = e.clientY;

    // Scroll down
    if (mouseY > rect.bottom - threshold) {
      container.scrollTop += speed;
    }

    // Scroll up
    if (mouseY < rect.top + threshold) {
      container.scrollTop -= speed;
    }
  };

  const getCategoryName = (categoryId: string): string => categories.find((cat) => cat._id.$oid === categoryId)?.name || "Unknown";

  const handleReorder = (sourceIndex: number, targetIndex: number, rowId: string | number) => {
    if (onReorderProduct) {
      onReorderProduct(String(rowId), sourceIndex, targetIndex);
    }
  };

  const rows: ProductRow[] = products.map((item, index) => ({
    id: item._id.$oid,
    sno: onReorderProduct ? (
      <span className="drag-handle flex items-center gap-1.5 cursor-grab active:cursor-grabbing hover:text-blue-600 transition-colors">
        <DragIndicatorIcon fontSize="small" className="text-gray-400 hover:text-blue-600 active:text-blue-700" />
        <span className="font-medium text-gray-700">{page * rowsPerPage + index + 1}</span>
      </span>
    ) : (
      <span className="font-medium text-gray-700">{page * rowsPerPage + index + 1}</span>
    ),
    productName: item.productName,
    arabicName: item.arabicName || "-",
    amount: Number(item.amount).toFixed(2),
    categoryName: getCategoryName(item.categoryId),

    imageUrl: <Image imagePath={item.imageUrl} className="w-[60px] h-[60px] flex justify-center items-center" />,
    Actions: (
      <>
        <Tooltip title="Edit" placement="bottom" arrow>
          <span>
            <IconButton disabled={showDeletedProducts} onClick={() => onEdit(item)} sx={{ color: showDeletedProducts ? "gray" : "black" }}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Delete" placement="bottom" arrow>
          <span>
            <IconButton disabled={showDeletedProducts} onClick={() => onDelete(item)} sx={{ color: showDeletedProducts ? "gray" : "red" }}>
              <DeleteOutline />
            </IconButton>
          </span>
        </Tooltip>
      </>
    ),
  }));

  // Define table headers
  const headCells: HeadCell<ProductRow>[] = [
    { id: "sno", label: "S.No" },
    { id: "productName", label: "Product Name" },
    { id: "arabicName", label: "Arabic Name" },
    { id: "amount", label: "Price (QAR)", numeric: true, align: "center" },
    { id: "categoryName", label: "Category" },
    { id: "imageUrl", label: "Image" },
    { id: "Actions", label: "Actions", align: "center" },
  ];

  return (
    <div className="thin-scrollbar w-full overflow-y-auto shadow-inner rounded-lg p-1 bg-white text-gray-900">
      <div ref={scrollRef} onDragOver={handleDragOver} className="thin-scrollbar w-full h-[65vh] overflow-y-auto shadow-inner rounded-lg p-1 bg-white text-gray-900">
        <CommonTable<ProductRow>
          data={rows}
          headCells={headCells}
          showToolbar={false}
          denseDefault={false}
          title="Products"
          pageSize={rowsPerPage}
          pagenation={false}
          selectable={false}
          className="w-full"
          onReorder={onReorderProduct ? handleReorder : undefined}
        />

        <div className="flex justify-end mr-8 mt-2">
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              onRowsPerPageChange(newSize);
              onPageChange(null, 0);
            }}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductTable;
